import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import {
  Star,
  ChevronLeft,
  ChevronRight,
  Quote,
  MessageCircle,
} from "lucide-react";

const WHATSAPP_LINK = `https://wa.me/971541873527?text=Hello%20Guru%20Maa,%20I%20saw%20the%20testimonials%20and%20would%20like%20to%20book%20a%20consultation.%20Please%20guide%20me.`;

const testimonials = [
  {
    id: 1,
    name: "Sarah Mitchell",
    location: "New York, USA",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100",
    rating: 5,
    text: "I was at a crossroads in my career when I consulted Guru Maa. Her predictions about my job change were incredibly accurate. Within 3 months, I received an offer from my dream company. Her guidance was truly life-changing!",
    service: "Career Guidance",
  },
  {
    id: 2,
    name: "James Thompson",
    location: "London, United Kingdom",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100",
    rating: 5,
    text: "After years of failed relationships, Guru Maa analyzed my birth chart and identified the planetary influences affecting my love life. Her remedies worked wonders - I found my soulmate within 6 months of following her guidance.",
    service: "Love & Marriage",
  },
  {
    id: 3,
    name: "Ahmed Al-Rashid",
    location: "Dubai, UAE",
    avatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100",
    rating: 5,
    text: "My business was struggling for years. Guru Maa suggested specific Vastu changes and a gemstone that aligned with my chart. The transformation was remarkable - my revenue tripled in just one year. Highly recommended!",
    service: "Vastu Consultation",
  },
  {
    id: 4,
    name: "Emily Watson",
    location: "Toronto, Canada",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100",
    rating: 5,
    text: "I was skeptical about astrology until I spoke with Guru Maa. She predicted events in my life with such precision that I became a believer. Her Yellow Sapphire recommendation brought unexpected financial gains and peace of mind.",
    service: "Gemstone Guidance",
  },
  {
    id: 5,
    name: "Michael Chen",
    location: "Sydney, Australia",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100",
    rating: 5,
    text: "Guru Maa helped me understand the delays in my marriage. Her Kundli analysis was spot-on, and the remedies she suggested were simple yet effective. I got married to a wonderful person exactly as she predicted!",
    service: "Kundli Matching",
  },
  {
    id: 6,
    name: "Olivia Parker",
    location: "Los Angeles, USA",
    avatar:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100",
    rating: 5,
    text: "What sets Guru Maa apart is her genuine care for her clients. She doesn't just give predictions - she provides practical solutions. Her horoscope reading gave me clarity about my life path that no other astrologer could provide.",
    service: "Horoscope Reading",
  },
  {
    id: 7,
    name: "David Williams",
    location: "Manchester, United Kingdom",
    avatar:
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100",
    rating: 5,
    text: "I consulted Guru Maa for my health concerns. She identified Saturn's influence on my well-being and recommended specific remedies. The improvement in my health has been remarkable. She is truly gifted!",
    service: "Health Consultation",
  },
  {
    id: 8,
    name: "Fatima Hassan",
    location: "Abu Dhabi, UAE",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100",
    rating: 5,
    text: "Guru Maa's accuracy is unmatched. She predicted my promotion, my husband's business success, and even warned us about a property deal that would have been problematic. We are forever grateful for her divine guidance.",
    service: "Family Consultation",
  },
];

// Hook to get card width based on screen size
const useCardWidth = () => {
  const [cardWidth, setCardWidth] = useState(null);

  useEffect(() => {
    const calculate = () => {
      const vw = window.innerWidth;
      if (vw < 640) {
        // mobile: 1 card full width (minus px-4 * 2 = 32px)
        setCardWidth(vw - 32);
      } else if (vw < 1024) {
        // sm/md: 2 cards with gap 24px
        setCardWidth((vw - 48 - 24) / 2);
      } else {
        // lg+: 3 cards — use CSS calc
        setCardWidth(null);
      }
    };
    calculate();
    window.addEventListener("resize", calculate);
    return () => window.removeEventListener("resize", calculate);
  }, []);

  return cardWidth;
};

const TestimonialCard = ({ testimonial, cardWidth }) => (
  <div
    className="testimonial-card flex-shrink-0 snap-start"
    style={
      cardWidth
        ? { width: `${cardWidth}px` }
        : { width: "calc((100% - 48px) / 3)" }
    }
    data-testid={`testimonial-${testimonial.id}`}
  >
    {/* Quote Icon */}
    <Quote className="text-[#D4AF37]/30 mb-4" size={40} />

    {/* Rating */}
    <div className="flex gap-1 mb-4">
      {[...Array(testimonial.rating)].map((_, i) => (
        <Star key={i} size={16} fill="#D4AF37" className="text-[#D4AF37]" />
      ))}
    </div>

    {/* Text */}
    <p className="text-[#F8FAFC] text-base leading-relaxed mb-6">
      "{testimonial.text}"
    </p>

    {/* Author */}
    <div className="flex items-center gap-3 flex-wrap">
      <img
        src={testimonial.avatar}
        alt={testimonial.name}
        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#D4AF37]/30 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-[#F8FAFC] font-semibold truncate">
          {testimonial.name}
        </p>
        <p className="text-[#D4AF37] text-sm font-medium truncate">
          {testimonial.location}
        </p>
      </div>
      <span className="text-[#94A3B8] text-xs px-2 sm:px-3 py-1 rounded-full border border-white/10 bg-white/5 whitespace-nowrap">
        {testimonial.service}
      </span>
    </div>
  </div>
);

const Testimonials = () => {
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const cardWidth = useCardWidth();

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

      // Update active dot on mobile
      if (cardWidth) {
        const idx = Math.round(scrollLeft / cardWidth);
        setActiveIndex(Math.min(idx, testimonials.length - 1));
      }
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      let scrollAmount;
      if (cardWidth) {
        scrollAmount = direction === "left" ? -cardWidth : cardWidth;
      } else {
        scrollAmount =
          direction === "left"
            ? -scrollRef.current.clientWidth / 3
            : scrollRef.current.clientWidth / 3;
      }
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setTimeout(checkScroll, 300);
    }
  };

  const scrollToIndex = (index) => {
    if (scrollRef.current && cardWidth) {
      scrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id="testimonials"
      className="relative py-20 md:py-32"
      data-testid="testimonials-section"
      ref={ref}
    >
      {/* Section Divider */}
      <div className="section-divider mb-20" />

      {/* Background Glow */}
      <div className="cosmic-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10 scale-150" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">
            Global Testimonials
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4">
            Trusted <span className="text-gold-gradient">Worldwide</span>
          </h2>
          <p className="mt-4">
            Discover how our guidance has transformed lives across the globe
          </p>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center gap-4 sm:gap-8 mb-12 flex-wrap"
        >
          {["USA", "UK", "Canada", "Australia", "UAE"].map((country) => (
            <span
              key={country}
              className="text-[#94A3B8] text-sm flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
              {country}
            </span>
          ))}
        </motion.div>

        {/* Testimonials Carousel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative"
        >
          {/* Navigation Buttons — always visible, spread on mobile, right-aligned on desktop */}
          <div className="flex justify-between sm:justify-end gap-2 mb-6">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`p-3 rounded-full border border-white/10 transition-all ${
                canScrollLeft
                  ? "hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#F8FAFC]"
                  : "opacity-30 cursor-not-allowed text-[#94A3B8]"
              }`}
              aria-label="Scroll left"
              data-testid="testimonials-scroll-left"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`p-3 rounded-full border border-white/10 transition-all ${
                canScrollRight
                  ? "hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#F8FAFC]"
                  : "opacity-30 cursor-not-allowed text-[#94A3B8]"
              }`}
              aria-label="Scroll right"
              data-testid="testimonials-scroll-right"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-6 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-4"
            style={{ scrollSnapType: "x mandatory" }}
          >
            {testimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                cardWidth={cardWidth}
              />
            ))}
          </div>

          {/* Mobile Dot Indicators */}
          <div className="flex sm:hidden justify-center gap-2 mt-5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                className={`transition-all duration-300 rounded-full ${
                  activeIndex === i
                    ? "w-6 h-2 bg-[#D4AF37]"
                    : "w-2 h-2 bg-white/20"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Mobile swipe hint */}
          <p className="sm:hidden text-center text-[#94A3B8]/50 text-xs mt-3">
            Swipe to read more testimonials
          </p>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-[#94A3B8] mb-6">
            Join thousands of satisfied clients worldwide.{" "}
            <span className="text-[#D4AF37]">
              Begin your transformation today.
            </span>
          </p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 whatsapp-btn px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold"
            data-testid="testimonials-cta"
          >
            <MessageCircle size={22} />
            <span>Book Your Consultation</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
